import { ModalButton } from '@/components/common/modal-button';
import { NearbyPoiItem } from '@/components/feature/floor-guide/nearby-poi-item';
import { PlayIcon, StopIcon } from '@/components/icons/floor-guide';
import { POI_ID_DELIMITER } from '@/constants/floor-guide';
import { useAccessibilityTTS, useTickSoundInteraction } from '@/hooks';
import { useFocusTrap } from '@/hooks/modal';
import { useConfigStore, useModalStackStore } from '@/stores';
import type { NearbyPoiItemWithDistanceType } from '@/types/map';
import { generateFloorName } from '@/utils/floor-guide';
import { shouldShowBuildingName } from '@/utils/shop';
import clsx from 'clsx';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

type Props = {
    poiList: NearbyPoiItemWithDistanceType[];
    selectedPoiId?: string;
    onSelectPoi: (poi: NearbyPoiItemWithDistanceType) => void;
    onStartNavigationGuide: (poi: NearbyPoiItemWithDistanceType) => void;
    onClose: () => void;
    isActive: boolean;
    title: string;
    floorName: string;
    buildingName: string;
    type?: 'category' | 'show-location';
};

export const NearbyPoiListDropdown = memo(
    ({ poiList, selectedPoiId, onSelectPoi, onStartNavigationGuide, onClose, isActive, title, floorName, buildingName, type = 'category' }: Props) => {
        const idModal = 'nearby-poi-list-dropdown-modal';
        const nearbyPoiListDropdownRef = useRef<HTMLDivElement>(null);
        const { t } = useTranslation();
        const language = useConfigStore((state) => state.config?.ui?.language);
        const buildings = useConfigStore((state) => state?.config?.server?.store?.buildings);
        const storeCode = useConfigStore((state) => state?.config?.server?.store?.storeCode);
        const { pushModal, popModal, isModalInStack } = useModalStackStore();

        const showBuilding = useMemo(
            () => shouldShowBuildingName(buildings, storeCode, language),
            [buildings, storeCode, language]
        );
        const { playTick } = useTickSoundInteraction();

        const { speak } = useAccessibilityTTS();

        const allPoiItems = useMemo(() => {
            if (!buildings) {
                return [];
            }
            return buildings.flatMap((item) => (Array.isArray(item?.poiContents) ? item?.poiContents : []));
        }, [buildings]);

        const getPoiItemByStudioPoiId = useCallback(
            (studioPoiId: string) => {
                if (!Array.isArray(allPoiItems) || !allPoiItems?.length) {
                    return undefined;
                }

                return allPoiItems.find((item) => item?.studioPoiId === studioPoiId);
            },
            [allPoiItems]
        );

        const handleStartNavigationGuide = () => {
            if (type === 'show-location' && Array.isArray(poiList) && poiList.length) {
                onStartNavigationGuide(poiList[0]);
                return;
            }

            const poiItem = poiList.find((poi) => `${poi?.id}${POI_ID_DELIMITER}${poi?.nearbyPoi?.id}` === selectedPoiId);
            if (!poiItem) {
                return;
            }

            onStartNavigationGuide(poiItem);
        };

        const hideSelectedStyles = Array.isArray(poiList) && poiList.length === 1;
        const showDistance = type === 'category' && Array.isArray(poiList) && poiList.length > 1;

        useEffect(() => {
            if (isActive) {
                // setTimeout(() => {
                if (!isModalInStack(idModal)) {
                    pushModal(idModal);
                }
                // }, 50);
            } else {
                if (isModalInStack(idModal)) {
                    popModal(idModal);
                }
            }

            return () => {
                if (isModalInStack(idModal)) {
                    popModal(idModal);
                }
            };
        }, [isActive, idModal, pushModal, popModal, isModalInStack]);

        useFocusTrap(nearbyPoiListDropdownRef, isActive, onClose, false, idModal, false);

        const headerTitle = `${title}, ${showBuilding ? buildingName + ', ' : ''}${t('floor_guide.text_to_speech.floor', { floorName: generateFloorName(floorName) })}`;

        return (
            <div className={styles['wrapper']} role="dialog" aria-modal="true" ref={nearbyPoiListDropdownRef}>
                <div
                    className={clsx(styles['popup__header'], 'focus-border')}
                    data-interactive
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            playTick();
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }}
                    aria-label={t('floor_guide.button.location_confirmation_tts')}
                    data-modal-region
                >
                    <h3 className={clsx(styles['popup__header__title'], 'text-text-main01 line-clamp-1 text-ellipsis')}>
                        {t('floor_guide.button.location_confirmation')}
                    </h3>
                </div>
                <div className={clsx(styles['header'], 'focus-border')}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            playTick();
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }}
                    data-interactive tabIndex={0} aria-label={headerTitle} data-modal-region>
                    <p className={clsx(styles['header__title'], 'line-clamp-1 text-ellipsis')}>{title}</p>
                    <span className={styles['header__building-floor']}>
                        {showBuilding && (
                            <span className={styles['header__building-floor__building']}>{buildingName}</span>
                        )}
                        <span className={styles['header__building-floor__floor']}>{floorName}</span>
                    </span>
                </div>
                <div className={clsx(styles['nearby-poi-list'], 'scrollbar-custom-gray-99938f scrollbar-w-4')}>
                    {poiList.map((poi, index) => {
                        const nearbyPoiId = poi?.nearbyPoi?.id;
                        if (!nearbyPoiId) {
                            return null;
                        }
                        const poiItem = getPoiItemByStudioPoiId(nearbyPoiId);

                        if (!poiItem) {
                            return null;
                        }

                        return (
                            <NearbyPoiItem
                                distance={poi?.distance}
                                showDistance={showDistance}
                                selectedPoiId={selectedPoiId}
                                key={poi?.id}
                                dataModalRegion={index === 0}
                                name={poiItem?.name?.[language] || poiItem?.name?.['ko'] || ''}
                                selected={`${poi?.id}${POI_ID_DELIMITER}${poi?.nearbyPoi?.id}` === selectedPoiId}
                                onSelect={() => onSelectPoi(poi)}
                                hideSelectedStyles={hideSelectedStyles}
                            />
                        );
                    })}
                </div>
                <div className={styles['actions']}>
                    <ModalButton
                        data-modal-region
                        size="sm"
                        icon={<PlayIcon />}
                        tabIndex={0}
                        role="button"
                        data-interactive
                        aria-label={t('floor_guide.button.direction_guide')}
                        data-role={t('common.data_role.button')}
                        onClick={handleStartNavigationGuide}
                    >
                        {t('floor_guide.button.direction_guide')}
                    </ModalButton>
                    <ModalButton
                        data-tts-scope="nearby-poi-list-button"
                        data-modal-region
                        size="sm"
                        icon={<StopIcon />}
                        variant="secondary"
                        tabIndex={0}
                        role="button"
                        data-interactive
                        data-role={t('common.data_role.button')}
                        onMouseDown={(e) => {
                            if (e.detail !== 0) {
                                e.preventDefault();
                            }
                        }}
                        onClick={() => {
                            speak(t('floor_guide.text_to_speech.location_confirm_has_been_completed'));
                            onClose();
                        }}
                        onFocus={() => {
                            speak(`${t('floor_guide.button.stop')} ${t('common.data_role.button')}`);
                        }}
                    >
                        {t('floor_guide.button.stop')}
                    </ModalButton>
                </div>
            </div>
        );
    }
);

NearbyPoiListDropdown.displayName = 'NearbyPoiListDropdown';
